import {
  type AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  Autocomplete as MuiAutocomplete,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { createFilterOptions } from "@mui/material/useAutocomplete";
import type React from "react";
import { useEffect, useState } from "react";
import type { FieldError } from "react-hook-form";

import useDebounce from "Infrastructure/Hooks/useDebounce";
import { usePrevious } from "Infrastructure/Hooks/usePrevious";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlDefaultButton } from "Shared/Buttons/BlDefaultButton";
import { ExpandArrowIcon } from "Shared/Icons";
import { SearchIcon } from "Shared/Icons/Search";
import { BlInput } from "Shared/Inputs/BlInput";
import type { BlOption } from "Shared/Inputs/BlOption";
import type { InputLabelProps } from "Shared/Inputs/InputLabel";
import { InputWrapper } from "Shared/Inputs/InputWrapper";

type BlAutocompleteProps = {
  options: BlOption[];
  value?: BlOption | null;
  allowCustomValue?: boolean;
  onOptionSelected?: (val: BlOption | null) => void;
  onInputValueChange?: (val: string) => void;
  onAddValue?: (val: string) => void;
  inputName?: string;
  placeholder?: string;
  isLoading?: boolean;
  debounceTime?: number;
  onSearch?: (searchString: string) => void;
  onBlur?: React.FocusEventHandler;
  disabled?: boolean;
  labelProps?: Omit<InputLabelProps, "htmlFor">;
  fieldError?: FieldError;
  fixedHeight?: boolean;
  sx?: SxProps<Theme>;
};

const filterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option: BlOption) => `${option.label} ${option.data ?? ""}`,
});

const PopupIcon = () => (
  <Box
    sx={{
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 25,
      height: 25,
      "& svg": { width: 12, height: "auto" },
    }}
  >
    <ExpandArrowIcon />
  </Box>
);

const CloseIcon = () => <Box sx={{ width: 25, height: 25 }} />;

export const BlAutocomplete = ({
  options: optionsProp,
  value: valueProp,
  allowCustomValue,
  onOptionSelected,
  onInputValueChange,
  onAddValue,
  inputName,
  placeholder,
  isLoading: isLoadingProp,
  debounceTime,
  onSearch,
  onBlur,
  disabled,
  labelProps,
  fieldError,
  fixedHeight,
  sx,
}: BlAutocompleteProps): React.ReactNode => {
  const { t } = useResource();

  const [autocompleteValue, setAutocompleteValue] = useState<BlOption | null>(
    null,
  );
  const [highlightedValue, setHighlightedValue] = useState<BlOption | null>(
    null,
  );
  const [inputValue, setInputValue] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(isLoadingProp);
  const [options, setOptions] = useState(optionsProp);

  const [searchTerm, setSearchTerm] = useState("");
  const prevSearchTerm = usePrevious(searchTerm);
  const [lastSearchTerm, setLastSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, debounceTime ?? 500);

  useEffect(() => {
    if (debouncedSearchTerm !== lastSearchTerm && onSearch) {
      onSearch(debouncedSearchTerm);
      setLastSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch, lastSearchTerm]);

  useEffect(() => {
    setIsLoading(isLoadingProp);
  }, [isLoadingProp]);
  useEffect(() => {
    setOptions(optionsProp);
  }, [optionsProp]);

  useEffect(() => {
    setAutocompleteValue((v) =>
      v?.value === valueProp?.value ? v : (valueProp ?? null),
    );
  }, [valueProp]);

  const onChange = (
    _: React.ChangeEvent<{}>,
    val: BlOption | string | null,
  ) => {
    setAutocompleteValue(val as BlOption);
    onOptionSelected?.(val as BlOption);
    setIsLoading(false);
    setHighlightedValue(null);
  };

  const onInputChange = (_: React.ChangeEvent<{}>, val: string | null) => {
    setInputValue(val ?? "");
    onInputValueChange?.(val ?? "");
    if (onSearch) {
      setSearchTerm((val as string) ?? "");
      if (prevSearchTerm && prevSearchTerm !== val) {
        setOptions([]);
      }
    }
  };

  const handleAddValue = () => {
    onAddValue?.(inputValue);
    setInputValue("");
    setAutocompleteValue(null);
  };

  const getInput = (params: AutocompleteRenderInputParams) => (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        "& > div": { width: "100%" },
      }}
    >
      <BlInput
        {...params}
        name={inputName}
        placeholder={placeholder}
        disabled={disabled}
        sx={{ ".MuiOutlinedInput-root": { padding: "0 0 0 8px" } }}
        slotProps={{
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && isOpen && (
                  <CircularProgress
                    size={25}
                    sx={{ position: "absolute", right: 50 }}
                  />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            startAdornment: (
              <Box display="flex" alignItems="center" sx={{ mx: 0.5 }}>
                <SearchIcon
                  style={{ width: "14px", height: "14px", opacity: 0.5 }}
                />
              </Box>
            ),
          },
        }}
      />
      {allowCustomValue && (
        <BlDefaultButton
          onClick={handleAddValue}
          sx={{
            position: "absolute",
            right: 10,
            top: 8,
            padding: "2px",
            borderRadius: "50%",
            "& svg": { width: 25, height: 25 },
          }}
        />
      )}
    </Box>
  );

  return (
    <InputWrapper
      labelProps={
        labelProps?.label ? { ...labelProps, htmlFor: inputName } : undefined
      }
      errorProps={{ error: fieldError, fixedHeight }}
    >
      <MuiAutocomplete
        value={autocompleteValue}
        inputValue={inputValue}
        options={options}
        onChange={onChange}
        onInputChange={onInputChange}
        renderInput={(params) => getInput(params)}
        filterOptions={filterOptions}
        forcePopupIcon
        getOptionLabel={(option) => (option as BlOption).label}
        getOptionKey={(option) => (option as BlOption).value.toString()}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        popupIcon={<PopupIcon />}
        clearIcon={allowCustomValue || isLoading ? null : <CloseIcon />}
        disabled={disabled}
        onOpen={() => {
          setIsOpen(true);
          if (onSearch) onSearch(debouncedSearchTerm);
        }}
        onClose={() => {
          setIsOpen(false);
          setSearchTerm("");
        }}
        autoHighlight
        clearText={t(Resources.Autocomplete.ClearText)}
        closeText={t(Resources.Autocomplete.CloseText)}
        openText={t(Resources.Autocomplete.OpenText)}
        noOptionsText={t(Resources.Autocomplete.NoOptionsText)}
        loadingText={t(Resources.Autocomplete.LoadingText)}
        loading={false}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            !highlightedValue && handleAddValue();
          }
        }}
        onHighlightChange={(_, val: BlOption | null) => {
          setHighlightedValue(val);
        }}
        onBlur={onBlur}
        sx={{
          "& .MuiAutocomplete-endAdornment": {
            right: `${allowCustomValue ? 50 : 4}px !important`,
            top: "50%",
            transform: "translateY(-50%)",
          },
          ...(allowCustomValue && {
            "& .MuiAutocomplete-clearIndicator": { padding: 0 },
          }),
          ...sx,
        }}
      />
    </InputWrapper>
  );
};
